import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { BroadcastifyFeed } from 'features/broadcastify/broadcastify.types';
import { useAuthState } from 'state/useAuthState';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { UserStatusChip } from 'components/UserStatusChip';
import { Link } from 'react-router-dom';
import { useBroadcastifyCounty } from 'features/broadcastify/hooks/useBroadcastifyCounty';
import { useBroadcastifyFeeds } from 'features/broadcastify/hooks/useBroadcastifyFeeds';
import { useState } from 'react';
import { LocationDescriptor } from 'history';
import { MEMBERSHIP_PLANS } from 'features/membership/membership.constants';
import { getPaywallLink } from 'features/membership/membership.utils';

type BroadcastifyFeedsProps = {
  lat: number;
  lng: number;
  withHeader?: boolean;
};

type BroadcastFeedProps = BroadcastifyFeed & {
  disabled: boolean;
  onClick: () => void;
  lat: number;
  lng: number;
};

const VISIBLE_FEEDS_PER_PAGE = 5;

const BroadcastFeed = (props: BroadcastFeedProps): JSX.Element => {
  const { feedId, name, listeners, online, disabled, onClick, lat, lng } =
    props;

  const getLinkLocation = (): LocationDescriptor => {
    const locationState = {
      fromRoute: window.location.pathname + window.location.search,
    };

    if (disabled) {
      const paywallLink = getPaywallLink({
        focusedPlan: MEMBERSHIP_PLANS.proYearly,
        source: 'broadcastify_feed',
      });
      return {
        pathname: paywallLink.pathname,
        search: paywallLink.search,
        state: locationState,
      };
    }

    return {
      pathname: `/feeds/${feedId}/${lat},${lng}`,
      state: locationState,
    };
  };

  return (
    <ListItemButton
      divider
      sx={{ '&.Mui-disabled': { opacity: 1 } }}
      component={Link}
      to={getLinkLocation()}
      onClick={onClick}
    >
      <ListItemText
        primary={name}
        secondary={
          <Chip
            label={`${listeners} listeners`}
            variant="watchduty"
            color={online ? 'success' : 'error'}
          />
        }
        slotProps={{
          primary: {
            sx: {
              fontWeight: 500,
              lineHeight: '22px',
              filter: disabled ? 'blur(3px)' : undefined,
            },
          },
          secondary: { component: 'div' },
        }}
      />
      <ListItemIcon
        sx={(theme) => ({
          minWidth: 'fit-content',
          color: theme.palette.text.primary,
        })}
      >
        <KeyboardArrowRightIcon />
      </ListItemIcon>
    </ListItemButton>
  );
};

export const BroadcastifyFeeds = (
  props: BroadcastifyFeedsProps,
): JSX.Element | null => {
  const { lat, lng, withHeader = true } = props;
  const { t } = useTranslation();
  const [visibleFeedsCount, setVisibleFeedsCount] = useState<number>(
    // Restore the visible feeds count from the history state and fallback to the default value
    window.history.state?.visibleFeedsCount ?? VISIBLE_FEEDS_PER_PAGE,
  );
  const { showMembershipProFeatures } = useAuthState();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');
  const { broadcastifyFeeds, isLoading: areFeedsLoading } =
    useBroadcastifyFeeds({ lat, lng });
  const { countyIsLoading, countyName, countyUrl } = useBroadcastifyCounty(
    lat,
    lng,
  );
  const isLoading = areFeedsLoading || countyIsLoading;
  const isCountyLinkShown = Boolean(
    countyName && countyUrl && showMembershipProFeatures,
  );

  return (
    <List
      subheader={
        withHeader ? (
          <ListSubheader variant="watchduty-section-header">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h3">
                {pinImprovements
                  ? t('feeds.label')
                  : t('geoEvent.structuredInfo.broadcastifyFeeds')}
              </Typography>
              <UserStatusChip type="pro" />
            </Stack>
          </ListSubheader>
        ) : undefined
      }
      disablePadding
    >
      {isLoading && (
        <ListItem divider role="status">
          <ListItemText
            primary={<Skeleton variant="text" width="80%" />}
            secondary={<Skeleton variant="text" width={80} />}
          />
        </ListItem>
      )}

      {!isLoading && !broadcastifyFeeds.length && !isCountyLinkShown && (
        <ListItem divider sx={{ py: 1.5 }}>
          <ListItemText
            primary={t('broadcastifyFeed.noFeeds')}
            slotProps={{
              primary: { sx: { fontWeight: 'fontWeightMedium' } },
            }}
          />
        </ListItem>
      )}

      {isCountyLinkShown && countyUrl && (
        <ListItemButton
          divider
          onClick={() =>
            window.open(countyUrl, '_blank', 'noopener,noreferrer')
          }
        >
          <ListItemText
            secondary={t('broadcastifyFeed.county', { countyName })}
            slotProps={{
              secondary: {
                color: 'textPrimary',
                sx: { fontWeight: 'fontWeightMedium' },
                variant: 'body1',
              },
            }}
            sx={{ alignContent: 'center', height: 42 }}
          />
          <OpenInNew />
        </ListItemButton>
      )}

      {broadcastifyFeeds.slice(0, visibleFeedsCount).map((feed) => (
        <BroadcastFeed
          key={feed.feedId}
          {...feed}
          disabled={!showMembershipProFeatures}
          // Save the current visible feeds count in the history state
          // so we can restore it when the user navigates back
          onClick={() =>
            window.history.replaceState(
              { visibleFeedsCount },
              '',
              window.location.pathname + window.location.search,
            )
          }
          lat={lat}
          lng={lng}
        />
      ))}

      {showMembershipProFeatures &&
        visibleFeedsCount < broadcastifyFeeds.length && (
          <ListItemButton
            onClick={() =>
              setVisibleFeedsCount((prev) => prev + VISIBLE_FEEDS_PER_PAGE)
            }
            divider
            disableRipple
            sx={{ ':hover': { backgroundColor: 'background.paper' } }}
          >
            <ListItemText
              primary={t('geoEvent.structuredInfo.showMore')}
              slotProps={{
                primary: {
                  sx: {
                    fontWeight: 'fontWeightBold',
                    textTransform: 'uppercase',
                    color: 'secondary.main',
                    textAlign: 'center',
                  },
                  variant: 'subtitle1',
                },
              }}
            />
          </ListItemButton>
        )}
    </List>
  );
};
